<template>
  <v-dialog
    :value="value"
    max-width="1050px"
    scrollable
    @input="$emit('input', $event)"
  >
    <v-card color="rgb(250, 250, 250)">
      <v-toolbar dense :color="colorBarraFormulario" dark flat>
        <v-icon class="mr-3" color="amber">mdi-star</v-icon>
        <v-toolbar-title>
          Valoraciones · {{ nombreSujeto }}
        </v-toolbar-title>
        <v-spacer />
        <v-chip
          v-if="resumen"
          class="mr-4 font-weight-bold"
          color="amber darken-2"
          text-color="blue-grey darken-4"
          small
        >
          {{ formatearValoracion(resumen.valoracion) }}
        </v-chip>
        <v-icon size="30" color="white" @click="cerrar">mdi-close-circle</v-icon>
      </v-toolbar>

      <v-card-text class="pa-6">
        <div v-if="loading" class="py-12 text-center">
          <v-progress-circular :color="colorAccent1" indeterminate />
          <div class="mt-3">Cargando valoraciones...</div>
        </div>

        <v-alert v-else-if="error" type="error" outlined>
          <div class="d-flex align-center">
            <span>{{ error }}</span>
            <v-spacer />
            <v-btn small text color="error" @click="cargarValoraciones">Reintentar</v-btn>
          </div>
        </v-alert>

        <template v-else-if="resumen">
          <v-row dense class="mb-4">
            <v-col cols="12" sm="4">
              <v-card outlined class="pa-4 text-center fill-height">
                <v-icon color="amber darken-2" size="30">mdi-star</v-icon>
                <div class="text-h5 font-weight-bold mt-1">
                  {{ formatearValoracion(resumen.valoracion) }}
                </div>
                <div class="text-caption text--secondary">Valoración general</div>
              </v-card>
            </v-col>
            <v-col cols="6" sm="4">
              <v-card outlined class="pa-4 text-center fill-height">
                <div class="text-h5 font-weight-bold">{{ resumen.total_valoraciones }}</div>
                <div class="text-caption text--secondary">Valoraciones recibidas</div>
              </v-card>
            </v-col>
            <v-col cols="6" sm="4">
              <v-card outlined class="pa-4 text-center fill-height">
                <div class="text-h5 font-weight-bold">{{ resumen.total_respuestas }}</div>
                <div class="text-caption text--secondary">Respuestas</div>
              </v-card>
            </v-col>
          </v-row>

          <v-alert v-if="!resumen.total_respuestas" type="info" outlined>
            {{ mensajeSinValoraciones }}
          </v-alert>

          <template v-else-if="tipo === 'reserva'">
            <valoraciones-seccion
              titulo="Rutas Meigas"
              icono="mdi-map"
              color="blue-grey darken-3"
              :resumen="resumenRutasMeigas"
            />
            <valoraciones-seccion
              titulo="Alojamientos"
              icono="mdi-home-city"
              color="deep-orange darken-2"
              :resumen="resumenAlojamientos"
              agrupar-por-proveedor
            />
            <valoraciones-seccion
              titulo="Servicios"
              icono="mdi-briefcase"
              color="teal darken-2"
              :resumen="resumenServicios"
              agrupar-por-proveedor
            />
          </template>

          <valoraciones-preguntas v-else :preguntas="preguntas" />
        </template>
      </v-card-text>

      <v-divider />
      <v-card-actions class="px-6 py-3">
        <v-spacer />
        <v-btn text :color="colorAccent1" @click="cerrar">Cerrar</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState } from "vuex";
import ValoracionesPreguntas from "@/components/valoraciones/ValoracionesPreguntas.vue";
import ValoracionesSeccion from "@/components/valoraciones/ValoracionesSeccion.vue";
import { filtrarResumenPorTipo } from "@/helpers/valoracionesHelpers";
import {
  obtenerValoracionesProveedor,
  obtenerValoracionesReserva,
} from "@/services/valoraciones";

export default {
  name: "ValoracionesDetalleDialog",

  components: {
    ValoracionesPreguntas,
    ValoracionesSeccion,
  },

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    sujeto: {
      type: Object,
      required: true,
    },
    tipo: {
      type: String,
      required: true,
      validator: (valor) => ["proveedor", "reserva"].includes(valor),
    },
  },

  data: () => ({
    loading: false,
    error: null,
    resumen: null,
  }),

  computed: {
    ...mapState(["colorAccent1", "colorBarraFormulario"]),
    ...mapState("auth", ["access_token"]),

    nombreSujeto() {
      if (this.tipo === "reserva") {
        return this.sujeto.codigo_reserva || this.sujeto.codigo || `Reserva #${this.sujeto.id}`;
      }

      return this.sujeto.nombre_alias || this.sujeto.nombre || "Proveedor";
    },

    mensajeSinValoraciones() {
      return this.tipo === "reserva"
        ? "Esta reserva todavía no tiene valoraciones."
        : "Este proveedor todavía no tiene valoraciones.";
    },

    preguntas() {
      return this.resumen && Array.isArray(this.resumen.preguntas)
        ? this.resumen.preguntas
        : [];
    },

    resumenRutasMeigas() {
      return filtrarResumenPorTipo(this.resumen, "general");
    },

    resumenAlojamientos() {
      return filtrarResumenPorTipo(this.resumen, "alojamiento");
    },

    resumenServicios() {
      return filtrarResumenPorTipo(this.resumen, "servicio");
    },
  },

  watch: {
    value(abierto) {
      if (abierto) {
        this.cargarValoraciones();
      }
    },
  },

  mounted() {
    if (this.value) {
      this.cargarValoraciones();
    }
  },

  methods: {
    async cargarValoraciones() {
      if (!this.sujeto.id) return;

      this.loading = true;
      this.error = null;

      try {
        this.resumen = this.tipo === "reserva"
          ? await obtenerValoracionesReserva(this.access_token, this.sujeto.id)
          : await obtenerValoracionesProveedor(this.access_token, this.sujeto.id);
        this.$emit("valoracion-cargada", this.resumen.valoracion);
      } catch (error) {
        this.error = this.tipo === "reserva"
          ? "No se pudieron cargar las valoraciones de la reserva."
          : "No se pudieron cargar las valoraciones del proveedor.";
      } finally {
        this.loading = false;
      }
    },

    cerrar() {
      this.$emit("input", false);
    },

    formatearValoracion(valoracion) {
      if (valoracion === null || valoracion === undefined) return "Sin valorar";
      return `${Number(valoracion).toFixed(1).replace(".", ",")} / 10`;
    },

  },
};
</script>
